/**
 * Dashboard URL bootstrap — parses `section`, `tab`, audit deep links, and delegates tx bootstrap.
 */
import { parseUrlBootstrap as parseTransactionBootstrap } from '../components/Dashboard/parseUrlBootstrap.js';
import {
  GROUP_DEFAULT_TAB,
  GROUP_TAB_IDS,
  isAnalyticsGroup,
  tabToGroup,
  type AnalyticsGroup,
  type AnalyticsTab as DomainAnalyticsTab,
} from '../../analytics/domain/analyticsTabs.js';
import {
  LEGACY_SHIPPING_METHODS_REMAP_SECTION,
  LEGACY_SHIPPING_METHODS_REMAP_TAB,
  remapLegacyDashboardSectionKey,
} from './remapLegacyDashboardSection.js';

export type CustomerExploreTab = 'events' | 'journeys' | 'cooccurrence' | 'drilldown';

/** Mirrors domain `AnalyticsTab` (13 tabs + advanced-benchmark allowlist). */
export type AnalyticsTab = DomainAnalyticsTab;

export type { AnalyticsGroup };

export type ConsentTab = 'overview' | 'dsr' | 'submit' | 'grantees';

/** Dashboard sections without extra URL params (path or `?section=` deep links). */
export type PlainDashboardSection =
  | 'account-settings'
  | 'bank-accounts'
  | 'inventory'
  | 'inventory-incidents'
  | 'products'
  | 'kiosks'
  | 'capabilities'
  | 'orders'
  | 'fulfillment'
  | 'tax-management'
  | 'donation-projects'
  | 'pickup-points'
  | 'shipping-methods'
  | 'loyalty-program'
  | 'promotions-program'
  | 'accounting-export'
  | 'reports'
  | 'command-center'
  | 'mission-control';

export type DashboardUrlBootstrap =
  | { section: PlainDashboardSection }
  | {
      section: 'analytics';
      tab: AnalyticsTab;
      group: AnalyticsGroup;
      exploreTab?: CustomerExploreTab;
    }
  | { section: 'audit-logs'; auditEventId?: string; correlationId?: string; openAuditDrawer?: boolean }
  | { section: 'consent-management'; tab: ConsentTab }
  | { section: 'paymentsHub'; caseKind?: 'inbound' | 'claim'; caseId?: string }
  | {
      section: 'transactions';
      txId?: string;
      isRefundCandidate?: 'true' | 'false';
      bankAccountIds?: string[];
    };

const CUSTOMER_EXPLORE_TABS: readonly CustomerExploreTab[] = [
  'events',
  'journeys',
  'cooccurrence',
  'drilldown',
];

/** All 13 analytics tabs including event-governance + advanced-benchmark. */
const ANALYTICS_TABS: readonly AnalyticsTab[] = [
  'revenue-summary',
  'revenue-kiosks',
  'revenue-timeseries',
  'revenue-categories',
  'revenue-products',
  'kiosk-product-performance',
  'payments',
  'funnel',
  'transactions',
  'ingest-quality',
  'event-governance',
  'advanced-explore',
  'advanced-benchmark',
];

const CONSENT_TABS: readonly ConsentTab[] = ['overview', 'dsr', 'submit', 'grantees'];

const PLAIN_DASHBOARD_SECTIONS: readonly PlainDashboardSection[] = [
  'account-settings',
  'bank-accounts',
  'inventory',
  'inventory-incidents',
  'products',
  'kiosks',
  'capabilities',
  'orders',
  'fulfillment',
  'tax-management',
  'donation-projects',
  'pickup-points',
  'shipping-methods',
  'loyalty-program',
  'promotions-program',
  'accounting-export',
  'reports',
  'command-center',
  'mission-control',
];

const PAYMENTS_HUB_CASE_KINDS = ['inbound', 'claim'] as const;

/** Path slug for payments hub (`DashboardSection` uses camelCase `paymentsHub`). */
export const PAYMENTS_HUB_PATH_SLUG = 'payments-hub';

/** First path segment after `/{tenant}/` that is not a dashboard section deep link. */
const RESERVED_ADMIN_PATH_SEGMENTS = new Set([
  'login',
  'invite',
  'forgot-password',
  'reset-password',
  'success-incident-centre',
  'outbox',
  'inbox',
  // Legacy path marker when still present before redirect strip.
  'admin',
]);

/** Allowed `/:tenant/:section` slugs (ADR-FE-G33-001 / MFE-v3-G-URL). */
export const DASHBOARD_PATH_SECTION_SLUGS: readonly string[] = [
  'account-settings',
  'bank-accounts',
  'inventory',
  'inventory-incidents',
  'products',
  'kiosks',
  'capabilities',
  'users',
  'analytics',
  'reports',
  'customer-explore',
  'transactions',
  'orders',
  'fulfillment',
  'audit-logs',
  'consent-management',
  'tax-management',
  'donation-projects',
  'pickup-points',
  'shipping-methods',
  'loyalty-program',
  'promotions-program',
  'accounting-export',
  PAYMENTS_HUB_PATH_SLUG,
  'command-center',
  'mission-control',
];

const DASHBOARD_PATH_SECTION_SLUG_SET = new Set<string>(DASHBOARD_PATH_SECTION_SLUGS);

export function isDashboardPathSectionSlug(slug: string): boolean {
  return DASHBOARD_PATH_SECTION_SLUG_SET.has(slug);
}

/**
 * True when `/{tenant}/…` is under the dashboard shell but is not a known section
 * or centre route — soft-404 should render in the main Outlet.
 */
export function isUnknownTenantAdminLeafPath(pathname: string): boolean {
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? '';
  const segments = pathOnly.split('/').filter((segment) => segment.length > 0);
  if (segments.length < 2) {
    return false;
  }
  const first = segments[0]?.toLowerCase();
  if (first === 'dev' || first === 'api' || first === 'admin') {
    return false;
  }
  if (
    /^\/(?!admin(?:\/|$))[^/]+\/(?:success-incident-centre|outbox)(?:\/|$)/.test(pathOnly) ||
    /^\/(?!admin(?:\/|$))[^/]+\/inbox\/?$/.test(pathOnly)
  ) {
    return false;
  }
  let sectionIndex = 1;
  if (segments[1] === 'admin') {
    sectionIndex = 2;
  }
  const section = segments[sectionIndex];
  if (section === undefined) {
    return false;
  }
  if (RESERVED_ADMIN_PATH_SEGMENTS.has(section)) {
    return false;
  }
  if (segments.length > sectionIndex + 1) {
    // Dashboard sections are single-segment; deeper paths are unknown.
    return true;
  }
  return !isDashboardPathSectionSlug(section);
}

/** Maps a path slug to the bootstrap `section` key (query param + DashboardSection). */
export function dashboardPathSlugToSectionKey(slug: string): string | null {
  if (!isDashboardPathSectionSlug(slug)) {
    return null;
  }
  if (slug === PAYMENTS_HUB_PATH_SLUG) {
    return 'paymentsHub';
  }
  // ADM-SCR-001 — /admin/users is the Users inventory path; gate as capabilities.
  if (slug === 'users') {
    return 'capabilities';
  }
  if (slug === 'customer-explore') {
    return 'customer-explore';
  }
  return slug;
}

/**
 * Maps a DashboardSection / bootstrap section key to a path slug for write sync.
 * ADM-TKT-0302 — products must write `/{tenant}/products`, not query-only drift.
 */
export function dashboardSectionKeyToPathSlug(sectionKey: string): string | null {
  if (sectionKey === 'paymentsHub') {
    return PAYMENTS_HUB_PATH_SLUG;
  }
  if (sectionKey === 'customer-explore') {
    return 'customer-explore';
  }
  return isDashboardPathSectionSlug(sectionKey) ? sectionKey : null;
}

/** Builds `/{tenant}/{slug}` for section path write (null when section has no path slug). */
export function buildDashboardSectionPathname(
  tenantCode: string,
  sectionKey: string,
): string | null {
  const slug = dashboardSectionKeyToPathSlug(sectionKey);
  if (slug === null || tenantCode.length === 0) {
    return null;
  }
  return `/${tenantCode}/${slug}`;
}

/** Reads the dashboard section slug from `/{tenant}/:section` (or legacy `/{tenant}/admin/:section`). */
export function parseDashboardPathSectionSlug(pathname?: string): string | null {
  const path =
    pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const segments = path.split('/').filter((segment) => segment.length > 0);
  if (segments.length < 2) {
    return null;
  }
  let sectionIndex = 1;
  if (segments[1] === 'admin') {
    sectionIndex = 2;
  }
  const segment = segments[sectionIndex];
  if (segment === undefined) {
    return null;
  }
  if (RESERVED_ADMIN_PATH_SEGMENTS.has(segment)) {
    return null;
  }
  return isDashboardPathSectionSlug(segment) ? segment : null;
}

/**
 * When the section is carried in the path, mirror it into `?section=` for entitlement
 * guards and legacy consumers that only read `location.search`.
 */
export function buildDashboardPathSectionSearch(pathname: string, search: string): string | null {
  const slug = parseDashboardPathSectionSlug(pathname);
  if (slug === null) {
    return null;
  }
  const sectionKey = dashboardPathSlugToSectionKey(slug);
  if (sectionKey === null) {
    return null;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('section') === sectionKey) {
    return null;
  }
  params.set('section', sectionKey);
  return params.toString();
}

function resolveBootstrapSectionKey(
  params: URLSearchParams,
  pathname?: string,
): string | null {
  const querySection = params.get('section');
  if (querySection !== null && querySection.length > 0) {
    if (querySection === PAYMENTS_HUB_PATH_SLUG) {
      return 'paymentsHub';
    }
    return querySection;
  }
  const pathSlug = parseDashboardPathSectionSlug(pathname);
  if (pathSlug === null) {
    return null;
  }
  return dashboardPathSlugToSectionKey(pathSlug);
}

function isPaymentsHubCaseKind(raw: string | null): raw is 'inbound' | 'claim' {
  return raw !== null && (PAYMENTS_HUB_CASE_KINDS as readonly string[]).includes(raw);
}

function isPlainDashboardSection(section: string): section is PlainDashboardSection {
  return (PLAIN_DASHBOARD_SECTIONS as readonly string[]).includes(section);
}

function isAuditEventId(raw: string | null): raw is string {
  return raw !== null && /^[1-9][0-9]{0,19}$/.test(raw);
}

function parseCustomerExploreTab(raw: string | null): CustomerExploreTab {
  return CUSTOMER_EXPLORE_TABS.includes(raw as CustomerExploreTab)
    ? (raw as CustomerExploreTab)
    : 'events';
}

function resolveAnalyticsTabForGroup(
  group: AnalyticsGroup,
  tabCandidate: AnalyticsTab | null,
): AnalyticsTab {
  if (tabCandidate !== null && (GROUP_TAB_IDS[group] as readonly string[]).includes(tabCandidate)) {
    return tabCandidate;
  }
  return GROUP_DEFAULT_TAB[group];
}

function parseAnalyticsBootstrap(
  params: URLSearchParams,
): Extract<DashboardUrlBootstrap, { section: 'analytics' }> {
  const tabRaw = params.get('tab');
  const groupRaw = params.get('group');
  const tabFromUrl = ANALYTICS_TABS.includes(tabRaw as AnalyticsTab)
    ? (tabRaw as AnalyticsTab)
    : null;

  let group: AnalyticsGroup;
  let tab: AnalyticsTab;

  if (isAnalyticsGroup(groupRaw)) {
    group = groupRaw;
    tab = resolveAnalyticsTabForGroup(group, tabFromUrl);
  } else if (tabFromUrl !== null) {
    // Legacy: missing `group` + has `tab` → infer group via tabToGroup
    tab = tabFromUrl;
    group = tabToGroup(tab);
  } else {
    group = 'revenue';
    tab = GROUP_DEFAULT_TAB.revenue;
  }

  const result: Extract<DashboardUrlBootstrap, { section: 'analytics' }> = {
    section: 'analytics',
    tab,
    group,
  };
  if (tab === 'advanced-explore') {
    result.exploreTab = parseCustomerExploreTab(params.get('exploreTab'));
  }
  return result;
}

/** Maps legacy `/command-center` (or `/admin/command-center`) paths to mission control. */
export function redirectLegacyCommandCenterPathname(
  pathname: string,
  search: string,
): { pathname: string; search: string } | null {
  const slug = parseDashboardPathSectionSlug(pathname);
  if (slug !== 'command-center') {
    return null;
  }
  const tenantMatch = /^\/([^/]+)(?:\/admin)?\/command-center/.exec(pathname);
  if (tenantMatch === null) {
    return null;
  }
  const nextSearch = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  nextSearch.set('section', 'mission-control');
  const nextSearchString = nextSearch.toString();
  return {
    pathname: `/${tenantMatch[1]}/mission-control`,
    search: nextSearchString.length > 0 ? `?${nextSearchString}` : '',
  };
}

/** Maps legacy `?section=command-center` URLs to mission control. */
export function redirectLegacyCommandCenterSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('section') !== 'command-center') {
    return null;
  }
  const next = new URLSearchParams(params);
  next.set('section', 'mission-control');
  return `?${next.toString()}`;
}

/**
 * G1 — maps legacy `/shipping-methods` (or `/admin/shipping-methods`) paths to
 * Account hub Delivery policy (`account-settings` + `tab=deliveryPolicy`).
 */
export function redirectLegacyShippingMethodsPathname(
  pathname: string,
  search: string,
): { pathname: string; search: string } | null {
  const slug = parseDashboardPathSectionSlug(pathname);
  if (slug !== 'shipping-methods') {
    return null;
  }
  const tenantMatch = /^\/([^/]+)(?:\/admin)?\/shipping-methods/.exec(pathname);
  if (tenantMatch === null) {
    return null;
  }
  const nextSearch = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  nextSearch.set('section', LEGACY_SHIPPING_METHODS_REMAP_SECTION);
  nextSearch.set('tab', LEGACY_SHIPPING_METHODS_REMAP_TAB);
  const nextSearchString = nextSearch.toString();
  return {
    pathname: `/${tenantMatch[1]}/${LEGACY_SHIPPING_METHODS_REMAP_SECTION}`,
    search: nextSearchString.length > 0 ? `?${nextSearchString}` : '',
  };
}

/**
 * G1 — maps legacy `?section=shipping-methods` URLs to Account hub Delivery policy.
 */
export function redirectLegacyShippingMethodsSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('section') !== 'shipping-methods') {
    return null;
  }
  const next = new URLSearchParams(params);
  next.set('section', LEGACY_SHIPPING_METHODS_REMAP_SECTION);
  next.set('tab', LEGACY_SHIPPING_METHODS_REMAP_TAB);
  return `?${next.toString()}`;
}

/** Maps legacy `/customer-explore` (or `/admin/customer-explore`) paths to analytics advanced explore. */
export function redirectLegacyCustomerExplorePathname(
  pathname: string,
  search: string,
): { pathname: string; search: string } | null {
  const slug = parseDashboardPathSectionSlug(pathname);
  if (slug !== 'customer-explore') {
    return null;
  }
  const tenantMatch = /^\/([^/]+)(?:\/admin)?\/customer-explore/.exec(pathname);
  if (tenantMatch === null) {
    return null;
  }
  const exploreTab = parseCustomerExploreTab(
    new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('tab'),
  );
  const nextSearch = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  nextSearch.set('section', 'analytics');
  nextSearch.set('group', 'advanced');
  nextSearch.set('tab', 'advanced-explore');
  nextSearch.set('exploreTab', exploreTab);
  const nextSearchString = nextSearch.toString();
  return {
    pathname: `/${tenantMatch[1]}/analytics`,
    search: nextSearchString.length > 0 ? `?${nextSearchString}` : '',
  };
}

/** Maps legacy `?section=customer-explore` URLs to analytics advanced explore. */
export function redirectLegacyCustomerExploreSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('section') !== 'customer-explore') {
    return null;
  }
  const exploreTab = parseCustomerExploreTab(params.get('tab'));
  const next = new URLSearchParams(params);
  next.set('section', 'analytics');
  next.set('group', 'advanced');
  next.set('tab', 'advanced-explore');
  next.set('exploreTab', exploreTab);
  return `?${next.toString()}`;
}

/**
 * F-AC-14 — when Explore is unavailable, either scrub the deep-link (pilot/capability
 * off) or preserve it for a recoverable locked tab (entitlement commercial lock).
 */
export interface RedirectDisabledAdvancedExplorePolicy {
  readonly advancedExploreEnabled: boolean;
  /**
   * When true, keep `advanced-explore` / `advanced-benchmark` / `group=advanced`
   * deep-links so the shell can show a locked tab (zero Explore API / no 403 loop).
   */
  readonly preserveLockedDeepLink?: boolean;
}

export function redirectDisabledAdvancedExploreSearch(
  search: string,
  advancedExploreEnabledOrPolicy: boolean | RedirectDisabledAdvancedExplorePolicy,
): string | null {
  const policy: RedirectDisabledAdvancedExplorePolicy =
    typeof advancedExploreEnabledOrPolicy === 'boolean'
      ? { advancedExploreEnabled: advancedExploreEnabledOrPolicy }
      : advancedExploreEnabledOrPolicy;
  if (policy.advancedExploreEnabled) {
    return null;
  }
  if (policy.preserveLockedDeepLink === true) {
    return null;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('section') !== 'analytics') {
    return null;
  }
  const tab = params.get('tab');
  const group = params.get('group');
  const isDisabledAdvancedTarget =
    tab === 'advanced-explore' || tab === 'advanced-benchmark' || group === 'advanced';
  if (!isDisabledAdvancedTarget) {
    return null;
  }
  const next = new URLSearchParams(params);
  next.set('group', 'revenue');
  next.set('tab', 'revenue-summary');
  next.delete('exploreTab');
  return `?${next.toString()}`;
}

export function parseDashboardUrlBootstrap(search?: string, pathname?: string): Partial<DashboardUrlBootstrap> {
  const source =
    search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const resolvedPathname =
    pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const params = new URLSearchParams(source.startsWith('?') ? source.slice(1) : source);
  const section = resolveBootstrapSectionKey(params, resolvedPathname);

  if (section === 'customer-explore') {
    const exploreTab = parseCustomerExploreTab(params.get('tab'));
    return {
      section: 'analytics',
      tab: 'advanced-explore',
      group: 'advanced',
      exploreTab,
    };
  }

  if (section === 'command-center') {
    return { section: 'mission-control' };
  }

  // G1 — legacy shipping-methods → Account hub (never left as resolved section).
  if (section === 'shipping-methods') {
    return { section: remapLegacyDashboardSectionKey(section) as 'account-settings' };
  }

  // ADM-SCR-001 inventory alias → capabilities (Users path) with SAG gates.
  if (section === 'users') {
    return { section: 'capabilities' };
  }

  if (section === 'analytics') {
    return parseAnalyticsBootstrap(params);
  }

  // Honor analytics deep links when `section` was stripped (legacy PWA shortcut
  // capture) but `group`/`tab` remain — otherwise header shows Data quality while
  // Overview stays selected and event-governance panel stays HTML-hidden.
  const tabRaw = params.get('tab');
  const groupRaw = params.get('group');
  if (
    section == null &&
    (ANALYTICS_TABS.includes(tabRaw as AnalyticsTab) || isAnalyticsGroup(groupRaw))
  ) {
    return parseAnalyticsBootstrap(params);
  }

  if (section === 'audit-logs') {
    const auditEventIdRaw = params.get('auditEventId');
    const correlationIdRaw = params.get('correlationId');
    const result: Extract<DashboardUrlBootstrap, { section: 'audit-logs' }> = {
      section: 'audit-logs',
    };
    if (isAuditEventId(auditEventIdRaw)) {
      result.auditEventId = auditEventIdRaw;
      result.openAuditDrawer = true;
    }
    if (correlationIdRaw && correlationIdRaw.length <= 128) {
      result.correlationId = correlationIdRaw;
    }
    return result;
  }

  if (section === 'consent-management') {
    const tabRaw = params.get('tab');
    const tab = CONSENT_TABS.includes(tabRaw as ConsentTab)
      ? (tabRaw as ConsentTab)
      : 'overview';
    return { section: 'consent-management', tab };
  }

  if (section === 'paymentsHub' || section === PAYMENTS_HUB_PATH_SLUG) {
    const caseKindRaw = params.get('caseKind');
    const caseIdRaw = params.get('caseId');
    const result: Extract<DashboardUrlBootstrap, { section: 'paymentsHub' }> = {
      section: 'paymentsHub',
    };
    if (isPaymentsHubCaseKind(caseKindRaw)) {
      result.caseKind = caseKindRaw;
    }
    if (caseIdRaw && caseIdRaw.trim().length > 0) {
      result.caseId = caseIdRaw.trim();
    }
    return result;
  }

  if (section === 'transactions') {
    const tx = parseTransactionBootstrap(source);
    return {
      section: 'transactions',
      ...(tx.txId !== undefined ? { txId: tx.txId } : {}),
      ...(tx.isRefundCandidate !== undefined
        ? { isRefundCandidate: tx.isRefundCandidate }
        : {}),
      ...(tx.bankAccountIds !== undefined
        ? { bankAccountIds: tx.bankAccountIds }
        : {}),
    };
  }

  if (section !== null && isPlainDashboardSection(section)) {
    return { section };
  }

  return {};
}
